/**
 * Club Owner Navigation Header - Dedicated navbar for club owners
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Radio, User, ArrowLeft, ShoppingCart, LogOut, Camera } from 'lucide-react';
import { Button } from './ui/button';
import { useAuthStore } from '../store';
import { toast } from 'sonner';
import { publicAPI } from '../services/api';

export const ClubNavbar = ({ showBack = false, title = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [clubName, setClubName] = useState(null);
  
  // Fetch club name if user has club_id
  useEffect(() => {
    const fetchClubName = async () => {
      if (user?.club_id) {
        try {
          const response = await publicAPI.getClub(user.club_id);
          setClubName(response.data.name);
        } catch (err) {
          console.error('Error fetching club:', err);
        }
      }
    };
    fetchClubName();
  }, [user?.club_id]);
  
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  
  // Check if profile/settings related page is active
  const isProfileActive = isActive('/club/settings') || isActive('/club/profile');
  
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/club');
    }
  };
  
  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Sesión cerrada');
  };
  
  return (
    <motion.header
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border"
    >
      <div className="flex items-center justify-between p-3 md:p-4 max-w-6xl mx-auto">
        {/* Left side - Logo (Home) or Back button + Title */}
        <div className="flex items-center gap-3">
          {showBack ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="shrink-0 h-10 w-10 md:h-11 md:w-11"
                data-testid="nav-back-btn"
              >
                <ArrowLeft className="w-6 h-6" />
              </Button>
              {title && (
                <h1 className="font-display font-bold text-lg md:text-xl">{title}</h1>
              )}
            </>
          ) : (
            /* Logo as Home button - navigates to club dashboard */
            <button
              onClick={() => navigate('/club')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              data-testid="nav-logo-home"
            >
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary flex items-center justify-center">
                <Building2 className="w-4 h-4 md:w-5 md:h-5 text-primary-foreground" />
              </div>
              <div className="hidden sm:block">
                <span className="font-display font-bold text-base md:text-lg">Clipper</span>
                {clubName ? (
                  <span className="text-xs text-primary block -mt-0.5">{clubName}</span>
                ) : (
                  <span className="text-xs text-muted-foreground block -mt-1">Club Panel</span>
                )}
              </div>
            </button>
          )}
        </div>
        
        {/* Right side - Navigation */}
        <nav className="flex items-center gap-1 md:gap-2">
          {/* Buttons Management */}
          <Button
            variant={isActive('/club/buttons') ? 'secondary' : 'ghost'}
            onClick={() => navigate('/club/buttons')}
            className="h-10 w-10 md:h-auto md:w-auto md:px-4 md:py-2 flex items-center [&_svg]:size-6 md:[&_svg]:size-5"
            data-testid="nav-buttons-btn"
          >
            <Radio className="shrink-0" />
            <span className="hidden md:inline ml-2 text-sm font-medium">Botones</span>
          </Button>
          
          {/* Cameras Management */}
          <Button
            variant={isActive('/club/cameras') ? 'secondary' : 'ghost'}
            onClick={() => navigate('/club/cameras')}
            className="h-10 w-10 md:h-auto md:w-auto md:px-4 md:py-2 flex items-center [&_svg]:size-6 md:[&_svg]:size-5"
            data-testid="nav-cameras-btn"
          >
            <Camera className="shrink-0" />
            <span className="hidden md:inline ml-2 text-sm font-medium">Cámaras</span>
          </Button>

          {/* Shop */}
          <Button
            variant={isActive('/club/shop') ? 'secondary' : 'ghost'}
            onClick={() => navigate('/club/shop')}
            className="h-10 w-10 md:h-auto md:w-auto md:px-4 md:py-2 flex items-center [&_svg]:size-6 md:[&_svg]:size-5"
            data-testid="nav-shop-btn"
          >
            <ShoppingCart className="shrink-0" />
            <span className="hidden md:inline ml-2 text-sm font-medium">Tienda</span>
          </Button>
          
          {/* Mi Cuenta (Profile + Settings merged) */}
          <Button
            variant={isProfileActive ? 'secondary' : 'ghost'}
            onClick={() => navigate('/club/settings')}
            className="h-10 w-10 md:h-auto md:w-auto md:px-4 md:py-2 flex items-center [&_svg]:size-6 md:[&_svg]:size-5"
            data-testid="nav-profile-btn"
          >
            <User className="shrink-0" />
            <span className="hidden md:inline ml-2 text-sm font-medium">Mi Cuenta</span>
          </Button>
          
          {/* Logout Button */}
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="h-10 w-10 md:h-auto md:w-auto md:px-4 md:py-2 flex items-center [&_svg]:size-6 md:[&_svg]:size-5 text-red-400 hover:text-red-500 hover:bg-red-500/10"
            data-testid="nav-logout-btn"
          >
            <LogOut className="shrink-0" />
            <span className="hidden md:inline ml-2 text-sm font-medium">Salir</span>
          </Button>
        </nav>
      </div>
    </motion.header>
  );
};

export default ClubNavbar;
